import { AxiosInstance } from "axios";
import classNames from "classnames/bind";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import {
  attemptShowErrorsInSnackbar,
  showSnackbarType,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { BaseButton, Card } from "@velocity-ehs/component-library";
import FormSkeleton from "../../sharedComponents/FormSkeleton";
import { Checkbox } from "../../sharedComponents/inputs/Checkbox";
import { Input } from "../../sharedComponents/inputs/Input";
import Select from "../../sharedComponents/inputs/Select";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import fieldTrim from "../../supportFunctions/FieldTrim";
import { applicationsListQuery } from "../../supportFunctions/graphql/ApplicationsQueries";
import {
  createSSOOutgoingMutation,
  deleteSSOOutgoingItemMutation,
  updateSSOOutgoingMutation,
} from "../../supportFunctions/graphql/SSOOutgoingQueries";
import { SPMetadata } from "./SPMetadata";
import classes from "./SPMetadataForm.module.scss";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import GatewayService from "../../../services/GatewayService";
import i18n from "../../../translations/i18n";

export enum Mode {
  Add,
  Edit,
  View,
}

type SPMetadataProps = {
  mode: Mode;
  metaData?: SPMetadata;
  isLoading?: boolean;
};

const SPMetadataForm = ({
  mode,
  isLoading,
  metaData,
}: SPMetadataProps): React.ReactElement => {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>(undefined);

  const [options, setOptions] = useState<Array<{ name: string; id: string }>>(
    []
  );

  let history = useHistory();
  const modificationMode: Mode = mode;

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SPMetadata>({
    mode: "onBlur",
    defaultValues: useMemo(() => {
      return { ...metaData, forCurrentCustomer: true };
    }, [metaData]),
  });

  const cx = classNames.bind(classes);

  const getSuggestions = async () => {
    const pagination = {
      pageNumber: 1,
      pageSize: 1000,
    };
    try {
      const response = await GatewayService.api.post(
        "",
        applicationsListQuery(null, pagination)
      );
      const options = response.data.data.applications.items;
      setOptions(options);
    } catch (error: any) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  };

  useEffect(() => {
    if (!options.length || !metaData) return;
    reset(metaData);
    const option = options.find(
      (option) => option.id === metaData.application.id
    );
    if (option) setSelectedOption(option);
    // eslint-disable-next-line
  }, [metaData, options]);

  useEffect(() => {
    getSuggestions();
    // eslint-disable-next-line
  }, []);

  const onSubmit = useCallback(
    (formValues: SPMetadata) => {
      const trimmedFieldData = fieldTrim({ formData: formValues });
      const data = {
        id: metaData?.id,
        entity: trimmedFieldData.entity,
        issuerX509Cert: trimmedFieldData.issuerX509Cert,
        destination: trimmedFieldData.destination,
        recipient: trimmedFieldData.recipient,
        acsUrl: trimmedFieldData.acsUrl,
        ribbonDesc: trimmedFieldData.ribbonDesc,
        issuerPrivateKey: trimmedFieldData.issuerPrivateKey,
        application: trimmedFieldData.application,
        forCurrentCustomer: formValues.forCurrentCustomer,
      } as SPMetadata;

      if (modificationMode === Mode.Add) {
        return addMetadata({
          metadata: data,
          showSnackbar: showSnackbar,
          graphQlAxios: GatewayService.api,
          customerId: UsersService.customer?.id ?? "",
          history: history,
        });
      }
      if (modificationMode === Mode.Edit) {
        return updateMetadata({
          metadata: data,
          graphQlAxios: GatewayService.api,
          showSnackbar: showSnackbar,
          reset: reset,
          formValues: trimmedFieldData,
        });
      }
    },
    // eslint-disable-next-line
    [metaData?.id, reset, showSnackbar]
  );

  const addButton = (
    <BaseButton
      type="submit"
      label={t("universal_Add")}
      form="applicationConfigForm"
      loading={isSubmitting}
    />
  );

  const cancelButton = (
    <BaseButton
      className="p-button-text"
      onClick={() => {
        if (history.length > 0) {
          history.goBack();
        } else {
          history.push("/applicationConfig");
        }
      }}
      label={t("universal_Cancel")}
    />
  );

  const editButtons = (
    <div className={cx(classes.actionButtons)}>
      <div className={cx(classes.buttonsWrapper)}>
        <BaseButton
          label={t("universal_Save")}
          type="submit"
          form="applicationConfigForm"
          loading={isSubmitting}
        />
        <BaseButton
          type="button"
          label={t("universal_Cancel")}
          className="p-button-text"
          onClick={() => history.push("/applicationConfig")}
        />
      </div>
    </div>
  );

  const form = (
    <Card
      title={t("application_Config_Application_Config_Details_Text")}
      headerAction={
        <BaseButton
          className="p-button-text"
          label={t("universal_Delete")}
          loading={isDeleting}
          visible={modificationMode === Mode.Edit}
          onClick={() =>
            deleteEntity({
              metadata: metaData,
              history: history,
              graphQlAxios: GatewayService.api,
              showSnackbar: showSnackbar,
              setIsDeleting: setIsDeleting,
            })
          }
        />
      }
    >
      {isLoading ? (
        <FormSkeleton rows={6} />
      ) : (
        <form id="applicationConfigForm" onSubmit={handleSubmit(onSubmit)}>
          <Select
            name="application"
            label={t("application_Config_Details")}
            value={selectedOption}
            options={options}
            control={control}
            required
            errorMessage={errors.application?.message}
            display="name"
            disabled={modificationMode !== Mode.Add}
          />
          <Input
            control={control}
            name="ribbonDesc"
            label={t("application_Config_Ribbon_Description")}
            rules={{
              required: t("required_message_ribbon_description"),
            }}
            errorMessage={errors ? errors.ribbonDesc?.message : undefined}
          />
          <Input
            control={control}
            name="entity"
            label={t("application_Config_Issuer")}
            rules={{
              required: t("required_message_issuer"),
            }}
            errorMessage={errors ? errors.entity?.message : undefined}
          />
          <Input
            control={control}
            name="acsUrl"
            label={t("application_Config_SP_ACS_URL")}
            rules={{
              required: t("required_message_acs_url"),
            }}
            errorMessage={errors ? errors.acsUrl?.message : undefined}
          />
          <Input
            control={control}
            name="recipient"
            label={t("application_Config_Recipient")}
            helpText={t("application_Config_Destination_Helper_Text")}
            errorMessage={errors ? errors.recipient?.message : undefined}
          />
          <Input
            control={control}
            name="destination"
            label={t("application_Config_Destination")}
            helpText={t("application_Config_Destination_Helper_Text")}
            errorMessage={errors ? errors.destination?.message : undefined}
          />
          <Checkbox
            control={control}
            name="forCurrentCustomer"
            label={t("application_Config_Metadata")}
            disabled={modificationMode !== Mode.Add}
          />

          {modificationMode === Mode.Add && (
            <div className={cx(classes.actionButtons)}>
              <div className={cx(classes.buttonsWrapper)}>
                {addButton}
                {cancelButton}
              </div>
            </div>
          )}
          {modificationMode === Mode.Edit && editButtons}
        </form>
      )}
    </Card>
  );

  return form;
};

export type UpdateMetadataProps = {
  metadata: SPMetadata;
  formValues: SPMetadata;
  graphQlAxios: AxiosInstance;
  showSnackbar: showSnackbarType | undefined;
  reset: (data: SPMetadata) => void;
};

export const updateMetadata = async ({
  metadata,
  graphQlAxios,
  showSnackbar,
  reset,
  formValues,
}: UpdateMetadataProps) => {
  try {
    await graphQlAxios.post("", updateSSOOutgoingMutation(metadata));
    showSnackbar &&
      showSnackbar(
        SnackbarType.Success,
        i18n.t("application_config_updated_successfully")
      );
  } catch (error) {
    showSnackbar &&
      showSnackbar(
        SnackbarType.Error,
        i18n.t("application_config_update_failed")
      );
  } finally {
    reset(formValues);
  }
};

export type AddMetadataProps = {
  metadata: SPMetadata;
  customerId: string;
  graphQlAxios: AxiosInstance;
  showSnackbar: showSnackbarType | undefined;
  history: any;
};

export const addMetadata = async ({
  metadata,
  customerId,
  graphQlAxios,
  showSnackbar,
  history,
}: AddMetadataProps) => {
  try {
    await graphQlAxios.post(
      "",
      createSSOOutgoingMutation(
        metadata,
        metadata.forCurrentCustomer ? customerId : null
      )
    );
    showSnackbar &&
      showSnackbar(
        SnackbarType.Success,
        i18n.t("application_config_added_successfully")
      );
    history.push("/applicationConfig");
  } catch (error) {
    attemptShowErrorsInSnackbar(showSnackbar, error);
  }
};

export type DeleteEntityProps = {
  metadata: SPMetadata | undefined;
  graphQlAxios: AxiosInstance;
  showSnackbar: showSnackbarType | undefined;
  setIsDeleting: (value: boolean) => void;
  history: any;
};

export const deleteEntity = async ({
  metadata,
  graphQlAxios,
  showSnackbar,
  setIsDeleting,
  history,
}: DeleteEntityProps) => {
  if (metadata) {
    try {
      setIsDeleting(true);
      await graphQlAxios.post("", deleteSSOOutgoingItemMutation(metadata.id));

      if (showSnackbar) {
        showSnackbar(
          SnackbarType.Success,
          i18n.t("application_config_profile_deleted_successfully")
        );
      }
      history.goBack();
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsDeleting(false);
    }
  }
};

export default SPMetadataForm;
